import Vue from 'vue'
import Vuex from 'vuex'
import Strapi from "strapi-sdk-js";

Vue.use(Vuex)

export default new Vuex.Store({
  state: {
    cinemaMode: false,
    user:{
      id: 1,
      projects: [],
    },
    region: localStorage.getItem('region') || 'eu',
    tasks: [],
    // Usage tracking pour les limites
    usage: {
      projectCount: 0,
      videoDurationUsed: 0, // en minutes
      speechToTextMinutes: 0,
      aiFilterUses: 0,
      exportCounts: {}, // { projectId: count }
      lastReset: null, // Date du dernier reset mensuel
    },
    // Cache des limites
    limits: {
      projectLimit: 2, // 2 projets gratuits (au lieu de 5)
      videoDurationLimit: 5,
      speechToTextLimit: 10,
      aiFilterLimit: 5,
      exportLimitPerProject: 3,
      hasWatermark: true,
      slowProcessing: false,
    }
  },
  getters: {
    GET_USER: state => {
      return state.user;
    },
    GET_USAGE: state => {
      return state.usage;
    },
    GET_LIMITS: state => {
      return state.limits;
    },
    // Vérifier si une limite est atteinte
    IS_LIMIT_REACHED: state => (limitType) => {
      switch(limitType) {
        case 'projects':
          return state.usage.projectCount >= state.limits.projectLimit;
        case 'videoDuration':
          return state.usage.videoDurationUsed >= state.limits.videoDurationLimit;
        case 'speechToText':
          return state.usage.speechToTextMinutes >= state.limits.speechToTextLimit;
        case 'aiFilter':
          return state.usage.aiFilterUses >= state.limits.aiFilterLimit;
        default:
          return false;
      }
    },
    // Obtenir le pourcentage d'utilisation
    GET_USAGE_PERCENTAGE: state => (limitType) => {
      switch(limitType) {
        case 'projects':
          return (state.usage.projectCount / state.limits.projectLimit) * 100;
        case 'videoDuration':
          return (state.usage.videoDurationUsed / state.limits.videoDurationLimit) * 100;
        case 'speechToText':
          return (state.usage.speechToTextMinutes / state.limits.speechToTextLimit) * 100;
        case 'aiFilter':
          return (state.usage.aiFilterUses / state.limits.aiFilterLimit) * 100;
        default:
          return 0;
      }
    }
  },
  mutations: {
    SET_TASKS: (state, tasks) => {
      state.tasks = tasks;
    },
    SET_USER_PROJECTS: (state, projects) => {
      state.user.projects = projects;
      state.usage.projectCount = projects.length;
    },
    SET_REGION: (state, region) => {
      state.user.region = region;
      localStorage.setItem('region', region);
      let api_url = '';
      if(region == 'eu')
      {
        api_url = import.meta.env.VITE_APP_API_URI || "http://api-2.voxdub.com";
      }else{
        api_url = 'https://api-us.voxdub.com';
      }
      Vue.prototype.$strapi = new Strapi({
        url: api_url
      });
    },
    SET_CINEMA_MODE: (state, cinemaMode) => {
      state.cinemaMode = cinemaMode;
    },
    // Mutations pour les limites
    SET_LIMITS: (state, limits) => {
      state.limits = { ...state.limits, ...limits };
      localStorage.setItem('voxdub_limits', JSON.stringify(state.limits));
    },
    SET_USAGE: (state, usage) => {
      state.usage = { ...state.usage, ...usage };
      localStorage.setItem('voxdub_usage', JSON.stringify(state.usage));
    },
    INCREMENT_VIDEO_DURATION: (state, minutes) => {
      state.usage.videoDurationUsed += minutes;
      localStorage.setItem('voxdub_usage', JSON.stringify(state.usage));
    },
    INCREMENT_SPEECH_TO_TEXT: (state, minutes) => {
      state.usage.speechToTextMinutes += minutes;
      localStorage.setItem('voxdub_usage', JSON.stringify(state.usage));
    },
    INCREMENT_AI_FILTER: (state) => {
      state.usage.aiFilterUses += 1;
      localStorage.setItem('voxdub_usage', JSON.stringify(state.usage));
    },
    INCREMENT_EXPORT: (state, projectId) => {
      if (!state.usage.exportCounts[projectId]) {
        state.usage.exportCounts[projectId] = 0;
      }
      state.usage.exportCounts[projectId] += 1;
      localStorage.setItem('voxdub_usage', JSON.stringify(state.usage));
    },
    RESET_MONTHLY_USAGE: (state) => {
      state.usage = {
        projectCount: state.user.projects.length,
        videoDurationUsed: 0,
        speechToTextMinutes: 0,
        aiFilterUses: 0,
        exportCounts: {},
        lastReset: new Date().toISOString(),
      };
      localStorage.setItem('voxdub_usage', JSON.stringify(state.usage));
    }
  },
  actions: {
    // set user projects 
    SET_USER_PROJECTS: ({commit}, projects) => {
      commit('SET_USER_PROJECTS', projects);
    },
    setRegion: ({commit}, region) => {
      commit('SET_REGION', region);
    },
    setCinemaMode: ({commit}, cinemaMode) => {
      commit('SET_CINEMA_MODE', cinemaMode);
    },
    setTasks: ({commit}, tasks) => {
      commit('SET_TASKS', tasks);
    },
    // Actions pour les limites
    updateLimits: ({commit}, limits) => {
      commit('SET_LIMITS', limits);
    },
    updateUsage: ({commit}, usage) => {
      commit('SET_USAGE', usage);
    },
    trackVideoDuration: ({commit}, minutes) => {
      commit('INCREMENT_VIDEO_DURATION', minutes);
    },
    trackSpeechToText: ({commit}, minutes) => {
      commit('INCREMENT_SPEECH_TO_TEXT', minutes);
    },
    trackAIFilter: ({commit}) => {
      commit('INCREMENT_AI_FILTER');
    },
    trackExport: ({commit}, projectId) => {
      commit('INCREMENT_EXPORT', projectId);
    },
    resetMonthlyUsage: ({commit}) => {
      commit('RESET_MONTHLY_USAGE');
    },
    // Charger l'usage depuis le localStorage
    loadUsageFromStorage: ({commit}) => {
      const savedUsage = localStorage.getItem('voxdub_usage');
      const savedLimits = localStorage.getItem('voxdub_limits');
      
      if (savedUsage) {
        try {
          const usage = JSON.parse(savedUsage);
          // Vérifier si on doit reset (nouveau mois)
          if (usage.lastReset) {
            const lastReset = new Date(usage.lastReset);
            const now = new Date();
            const daysSinceReset = (now - lastReset) / (1000 * 60 * 60 * 24);
            
            if (daysSinceReset >= 30) {
              commit('RESET_MONTHLY_USAGE');
            } else {
              commit('SET_USAGE', usage);
            }
          } else {
            commit('SET_USAGE', usage);
          }
        } catch (err) {
          console.error('Error loading usage from storage:', err);
        }
      }
      
      if (savedLimits) {
        try {
          const limits = JSON.parse(savedLimits);
          commit('SET_LIMITS', limits);
        } catch (err) {
          console.error('Error loading limits from storage:', err);
        }
      }
    }
  },
  modules: {
  }
})
